import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  FileText
} from "lucide-react";
import * as ownerApi from "../../api/payroll/overtimeRuleApi";
import * as portalApi from "../../api/payroll/overtimeRulePortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function OvertimeRuleList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/overtime-rules" : "/overtime-rules";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getAllOvertimeRules();
        setList(Array.isArray(res) ? res : res?.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isPortal, api]);

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = list.map((row) => ({ ...row, id: row._id }));

  const columns = [
    {
      key: "name",
      header: "Rule Name",
      render: (row) => <span className="font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</span>,
    },
    {
      key: "calculationType",
      header: "Type",
      render: (row) => (
        <Badge variant="default" className="font-mono">
          {row.calculationType}
        </Badge>
      ),
    },
    {
      key: "rate",
      header: "Rate / Value",
      render: (row) => (
        <span className="font-semibold text-neutral-800 dark:text-neutral-100">
          {row.calculationType === "FIXED_PER_HOUR"
            ? `₹${row.fixedRatePerHour}/hr`
            : `${row.multiplier}x`}
        </span>
      ),
    },
    {
      key: "isDefault",
      header: "Default",
      render: (row) => <Badge variant={row.isDefault ? "primary" : "default"}>{row.isDefault ? "Yes" : "No"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (row) => (
        <Link to={`${base}/edit/${row._id}`}>
          <IconButton aria-label="Edit overtime rule" variant="ghost" size="sm">
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </Link>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      {/* Top Header */}
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Overtime Rules
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Manage calculation methods, hourly multipliers, and limits for overtime pay
          </p>
        </div>

        <Link to={`${base}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add Rule
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={FileText}
                title="No overtime rules found"
                description="Create rules to automate overtime pay calculations."
                action={
                  <Link to={`${base}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add Rule
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}